import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFirebaseAuth, getFirestoreDb } from '../utils/firebase';
import { CLOUD_FEATURES_ENABLED } from '../constants/config';
import { storage } from '../utils/storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { MESSAGE_TYPES } from '../constants/background';
import { pushBillingAuth } from '../services/billingAuth';
import { getPlatform } from '../infrastructure/platform';

/**
 * Auth context, backed by Firebase Authentication — replaces the retired
 * mock auth (and the earlier Clerk integration).
 *
 * Consumers across the app read `currentUser` / `session`; the sync client gets
 * its bearer token from `getIdToken`. Email/password sign-in UI lives in
 * `OnlineSyncAccount`.
 *
 * Two modes, selected without conditional hooks:
 *  - Cloud configured (API URL set) → `FirebaseAuthProvider` subscribes to
 *    Firebase auth state.
 *  - Not configured → a static signed-out provider keeps the local-only
 *    extension fully functional.
 */

export interface AuthUser {
  id: string;
  email?: string;
}

export interface AuthSession {
  userId: string;
}

interface AuthContextType {
  currentUser: AuthUser | null;
  session: AuthSession | null;
  isLoading: boolean;
  isCloudEnabled: boolean;
  /** Current Firebase ID token, or null when signed out. */
  getIdToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
}

const SIGNED_OUT: AuthContextType = {
  currentUser: null,
  session: null,
  isLoading: false,
  isCloudEnabled: false,
  getIdToken: async () => null,
  signOut: async () => {},
};

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

function FirebaseAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, next => {
      setUser(next);
      setIsLoading(false);
      // Mirror uid/ID token/refresh token into chrome.storage so the
      // background (which never initialises the Firebase SDK) can read the
      // billing doc over the Firestore REST API. Clears on sign-out.
      void pushBillingAuth(next);
    });
    return unsubscribe;
  }, []);

  // Real-time premium unlock: while this UI surface is open, watch the
  // server-written subscription doc and nudge the background to reconcile the
  // moment the Stripe webhook lands. The background itself must not hold a
  // long-lived listener (MV3 suspends it), so the durable fallbacks remain the
  // startup / UI_READY / hourly-alarm triggers.
  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(
      doc(getFirestoreDb(), 'users', user.uid, 'billing', 'subscription'),
      () => {
        void getPlatform()
          .bus.send({ type: MESSAGE_TYPES.BILLING_REFRESH })
          .catch(() => {
            // Background not ready / context torn down — the alarm path covers it.
          });
      },
      () => {
        // Snapshot errors (offline, rules) are non-fatal; polling covers it.
      }
    );
    return unsubscribe;
  }, [user]);

  const value = useMemo<AuthContextType>(
    () => ({
      currentUser: user ? { id: user.uid, email: user.email ?? undefined } : null,
      session: user ? { userId: user.uid } : null,
      isLoading,
      isCloudEnabled: true,
      getIdToken: async () => (user ? user.getIdToken() : null),
      signOut: async () => {
        await firebaseSignOut(getFirebaseAuth());
        // Account-switch policy: local library and sync scope belong to the
        // account that created them. Clearing here prevents the next sign-in
        // from inheriting this account's data (cross-account leak) or its pull
        // cursor (missed pulls). The cloud copy remains the durable one.
        await Promise.all([
          storage.remove(STORAGE_KEYS.PROMPTS),
          storage.remove(STORAGE_KEYS.FOLDERS),
          storage.remove(STORAGE_KEYS.API_SYNC_STATE),
        ]);
      },
    }),
    [user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  if (!CLOUD_FEATURES_ENABLED) {
    return <AuthContext.Provider value={SIGNED_OUT}>{children}</AuthContext.Provider>;
  }
  return <FirebaseAuthProvider>{children}</FirebaseAuthProvider>;
}
